import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Stethoscope, Loader2, CheckCircle2, AlertTriangle, XCircle, Copy, X, Check } from 'lucide-react';

/**
 * Admin-only diagnostic tool. Accepts a station ID and runs both
 * the Stream Monitor recording diagnostic AND the Songs Analyzer
 * station diagnostic, showing a consolidated report.
 *
 * Usage:
 *   <StationDiagnosticTool apiBase={API} token={token} userId="spiros" />
 */
const StationDiagnosticTool = ({ apiBase, token, userId: userIdProp }) => {
  const [open, setOpen] = useState(false);
  const [stationId, setStationId] = useState('');
  const [hours, setHours] = useState(168);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // Derive username from JWT if not provided
  const userId = React.useMemo(() => {
    if (userIdProp) return userIdProp;
    try {
      if (!token) return 'admin';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || payload.sub || payload.user_id || 'admin';
    } catch (_) {
      return 'admin';
    }
  }, [userIdProp, token]);

  const runDiagnostic = async () => {
    if (!stationId.trim()) {
      setError('Δώσε station ID');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      // v2.9.147 (2026-06-08) — switched from native fetch to axios.
      // The rrweb session-recording library injects a fetch wrapper
      // that calls `response.clone()` AFTER our `await resp.json()`
      // has already drained the body, producing the cryptic
      // "Failed to execute 'clone' on 'Response': Response body is
      // already used" error in the diagnostic panel.
      // axios uses XHR and bypasses the wrapper entirely.
      const hdr = { Authorization: `Bearer ${token}` };
      const [analyzerRes, monitorRes] = await Promise.allSettled([
        axios.get(
          `${apiBase}/api/songs-analyzer/station-diagnostic`,
          {
            params: {
              station_id: stationId.trim(),
              user_id: userId,
              hours,
            },
            headers: hdr,
            timeout: 30000,
          },
        ),
        axios.get(
          `${apiBase}/api/stream-monitor/station-recording-diagnostic/${encodeURIComponent(stationId.trim())}`,
          { headers: hdr, timeout: 30000 },
        ),
      ]);

      const _pick = (r) => {
        if (r.status === 'fulfilled') return r.value.data;
        const err = r.reason || {};
        const resp = err.response || {};
        return {
          error: resp.status
            ? `${resp.status} ${resp.statusText || ''}`.trim()
            : (err.message || 'Network error'),
          detail: resp.data,
        };
      };

      setResult({
        analyzer: _pick(analyzerRes),
        monitor: _pick(monitorRes),
      });
    } catch (e) {
      setError(e.message || 'Σφάλμα');
    } finally {
      setLoading(false);
    }
  };

  const copyJson = async (key = 'header') => {
    if (!result) return;
    const payload = JSON.stringify(result, null, 2);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        // Fallback for non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = payload;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    } catch (_) {
      // noop — silent failure is fine, user can still copy from the <pre>
    }
  };

  // ─── Status classification helpers ───
  const statusOf = (val, { good, warn, bad } = {}) => {
    if (val === null || val === undefined) return 'unknown';
    if (typeof good === 'function' && good(val)) return 'good';
    if (typeof warn === 'function' && warn(val)) return 'warn';
    if (typeof bad === 'function' && bad(val)) return 'bad';
    return 'neutral';
  };

  const StatusIcon = ({ status }) => {
    const map = {
      good:    <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
      warn:    <AlertTriangle className="w-4 h-4 text-amber-400" />,
      bad:     <XCircle className="w-4 h-4 text-rose-400" />,
      unknown: <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-500" />,
      neutral: <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />,
    };
    return map[status] || map.neutral;
  };

  const analyzer = result?.analyzer;
  // v2.9.147 — wrap in useMemo so the linter recognises this as a
  // hook-computed runtime value and stops complaining about
  // `react-hooks/static-components`.
  const monitorPayload = useMemo(() => result?.monitor, [result]);

  return (
    <div className="mb-3">
      {!open ? (
        <button
          data-testid="open-station-diagnostic"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 border border-indigo-500/40 transition-colors"
        >
          <Stethoscope className="w-3.5 h-3.5" />
          Διάγνωση Σταθμού (με ID)
        </button>
      ) : (
        <div className="bg-gray-900/60 border border-indigo-500/30 rounded-xl p-4 relative">
          <button
            onClick={() => { setOpen(false); setResult(null); setError(null); }}
            className="absolute top-2 right-2 p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300"
            aria-label="Κλείσιμο"
            data-testid="close-station-diagnostic"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 mb-3">
            <Stethoscope className="w-4 h-4 text-indigo-300" />
            <span className="text-sm font-semibold text-indigo-200">Διάγνωση Σταθμού</span>
            <span className="text-[10px] text-gray-500">— δώσε station ID για πλήρη αναφορά</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              data-testid="diag-station-id-input"
              value={stationId}
              onChange={(e) => setStationId(e.target.value)}
              placeholder="698dce57b7cadc81d298b48b"
              className="flex-1 min-w-[250px] bg-gray-800 border border-white/10 rounded-md px-3 py-1.5 text-sm text-gray-200 font-mono focus:border-indigo-400 focus:outline-none"
              onKeyDown={(e) => e.key === 'Enter' && runDiagnostic()}
            />
            <select
              data-testid="diag-hours-select"
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="bg-gray-800 border border-white/10 rounded-md px-2 py-1.5 text-xs text-gray-300"
            >
              <option value={24}>24h</option>
              <option value={72}>3 ημέρες</option>
              <option value={168}>7 ημέρες</option>
              <option value={720}>30 ημέρες</option>
            </select>
            <button
              data-testid="run-diagnostic-btn"
              onClick={runDiagnostic}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-indigo-500 hover:bg-indigo-600 text-white font-medium disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stethoscope className="w-3.5 h-3.5" />}
              {loading ? 'Τρέχει…' : 'Έλεγχος'}
            </button>
          </div>

          {error && (
            <div className="mt-3 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded p-2">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-4 space-y-4">
              {/* Header row */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold text-gray-100">
                    {analyzer?.station_name || monitorPayload?.station_name || '—'}
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono">{analyzer?.station_id || stationId}</div>
                </div>
                <button
                  onClick={() => copyJson('header')}
                  className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border transition-colors ${
                    copiedKey === 'header'
                      ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
                      : 'text-gray-300 border-white/10 bg-gray-800/60 hover:text-white hover:border-white/20 hover:bg-gray-800'
                  }`}
                  data-testid="copy-diag-json"
                  title="Αντιγραφή πλήρους JSON στο clipboard"
                >
                  {copiedKey === 'header' ? (
                    <>
                      <Check className="w-3 h-3" /> Αντιγράφηκε!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" /> Αντιγραφή JSON
                    </>
                  )}
                </button>
              </div>

              {/* Unified diagnostic cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Recording / Polling */}
                <div className="bg-gray-800/50 rounded-lg border border-white/5 p-3">
                  <div className="text-[11px] text-gray-400 font-medium mb-2">Καταγραφή / Polling</div>
                  <Row
                    label="Ενεργός σταθμός"
                    value={analyzer?.active ? 'Ναι' : 'Όχι'}
                    icon={<StatusIcon status={analyzer?.active ? 'good' : 'bad'} />}
                  />
                  <Row
                    label="Data mode"
                    value={analyzer?.data_mode}
                    icon={<StatusIcon status="neutral" />}
                  />
                  <Row
                    label="Stream logs (24-720h)"
                    value={analyzer?.stream_logs_count}
                    icon={<StatusIcon status={statusOf(analyzer?.stream_logs_count, { good: v => v > 100, warn: v => v > 0, bad: v => v === 0 })} />}
                  />
                  <Row
                    label="Unique titles"
                    value={analyzer?.unique_titles}
                    icon={<StatusIcon status={statusOf(analyzer?.unique_titles, { good: v => v > 20, warn: v => v > 0, bad: v => v === 0 })} />}
                  />
                  <Row
                    label="Songs (1h)"
                    value={
                      monitorPayload?.metadata_stuck && monitorPayload?.songs?.songs_1h === 0
                        ? `${monitorPayload.songs.songs_1h} · stuck`
                        : monitorPayload?.songs?.songs_1h
                    }
                    icon={
                      <StatusIcon
                        status={
                          // If station is stuck, downgrade severity from "bad" to "warn"
                          // since the system is healthy — it's the source that's frozen.
                          monitorPayload?.metadata_stuck && monitorPayload?.songs?.songs_1h === 0
                            ? 'warn'
                            : statusOf(monitorPayload?.songs?.songs_1h, { good: v => v > 3, warn: v => v > 0, bad: v => v === 0 })
                        }
                      />
                    }
                  />
                  <Row
                    label="Songs (24h)"
                    value={monitorPayload?.songs?.songs_24h}
                    icon={<StatusIcon status={statusOf(monitorPayload?.songs?.songs_24h, { good: v => v > 50, warn: v => v > 0, bad: v => v === 0 })} />}
                  />
                  {monitorPayload?.songs?.last_song_age_seconds != null && (
                    <Row
                      label="Τελευταίο τραγούδι (age)"
                      value={formatAge(monitorPayload.songs.last_song_age_seconds)}
                      icon={<StatusIcon status={statusOf(monitorPayload.songs.last_song_age_seconds, { good: v => v < 1800, warn: v => v < 7200, bad: v => v >= 7200 })} />}
                    />
                  )}
                  {(monitorPayload?.metadata_stuck != null || monitorPayload?.off_zone != null) && (
                    <Row
                      label="Metadata status"
                      value={
                        monitorPayload?.off_zone
                          ? `Off-zone (${monitorPayload?.schedule_start_time || '06:00'}–${monitorPayload?.schedule_end_time || '21:00'})`
                          : monitorPayload?.metadata_stuck
                          ? 'Stuck (πηγή παγωμένη)'
                          : 'OK'
                      }
                      icon={
                        <StatusIcon
                          status={
                            monitorPayload?.off_zone ? 'good' : monitorPayload?.metadata_stuck ? 'warn' : 'good'
                          }
                        />
                      }
                    />
                  )}
                  {monitorPayload?.inferred_play?.stuck_splits_today != null && monitorPayload.inferred_play.stuck_splits_today > 0 && (
                    <Row
                      label="Heartbeat splits (σήμερα)"
                      value={monitorPayload.inferred_play.stuck_splits_today}
                      icon={<StatusIcon status="warn" />}
                    />
                  )}
                </div>

                {/* Listeners / Activity */}
                <div className="bg-gray-800/50 rounded-lg border border-white/5 p-3">
                  <div className="text-[11px] text-gray-400 font-medium mb-2">Ακροατές & Activity</div>
                  <Row
                    label="Listener rows (σύνολο)"
                    value={analyzer?.listener_hourly_stats_count}
                    icon={<StatusIcon status={statusOf(analyzer?.listener_hourly_stats_count, { good: v => v > 20, warn: v => v > 0, bad: v => v === 0 })} />}
                  />
                  <Row
                    label="Last listener peak"
                    value={analyzer?.last_listener_row?.peak}
                    icon={<StatusIcon status={statusOf(analyzer?.last_listener_row?.peak, { good: v => v > 10, warn: v => v > 0, bad: v => v === 0 })} />}
                  />
                  <Row
                    label="Activity (24h TLH)"
                    value={analyzer?.activity_last_24h?.tlh}
                    icon={<StatusIcon status={statusOf(analyzer?.activity_last_24h?.tlh, { good: v => v > 100, warn: v => v > 0, bad: v => v === 0 })} />}
                  />
                  <Row
                    label="Activity samples"
                    value={analyzer?.activity_last_24h?.samples}
                    icon={<StatusIcon status={statusOf(analyzer?.activity_last_24h?.samples, { good: v => v > 5, warn: v => v > 0, bad: v => v === 0 })} />}
                  />
                  {analyzer?.ranking_sim_7d && (
                    <Row
                      label="Ranking TLH (7d sim)"
                      value={analyzer.ranking_sim_7d.tlh_raw}
                      icon={<StatusIcon status={statusOf(analyzer.ranking_sim_7d.tlh_raw, { good: v => v > 50, warn: v => v > 0, bad: v => v === 0 })} />}
                    />
                  )}
                </div>
              </div>

              {/* Monitor diagnoses */}
              {Array.isArray(monitorPayload?.diagnosis) && monitorPayload.diagnosis.length > 0 && (
                <div className="bg-gray-800/50 rounded-lg border border-white/5 p-3">
                  <div className="text-[11px] text-gray-400 font-medium mb-2">Stream Monitor Diagnoses</div>
                  <ul className="space-y-1 text-[11px]">
                    {monitorPayload.diagnosis.map((d, i) => {
                      const level = d.startsWith('PROBLEM') ? 'bad' : d.startsWith('WARNING') ? 'warn' : 'neutral';
                      return (
                        <li key={i} className="flex items-start gap-2">
                          <StatusIcon status={level} />
                          <span className="text-gray-300">{d}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Raw JSON collapsible */}
              <details className="bg-gray-800/50 rounded-lg border border-white/5">
                <summary className="cursor-pointer px-3 py-2 text-[11px] text-gray-400 hover:text-gray-200">
                  Πλήρες JSON (advanced)
                </summary>
                <pre className="px-3 pb-3 text-[10px] text-gray-400 overflow-x-auto max-h-96 font-mono whitespace-pre-wrap break-all">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, icon }) => (
  <div className="flex items-center justify-between gap-2 text-[11px] py-1 border-b border-white/5 last:border-0">
    <span className="inline-flex items-center gap-2 text-gray-400">
      {icon}
      {label}
    </span>
    <span className="text-gray-200 font-medium tabular-nums">{fmtVal(value)}</span>
  </div>
);

const fmtVal = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return v.toLocaleString('el-GR');
  return String(v);
};

const formatAge = (seconds) => {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m`;
  return `${Math.floor(seconds)}s`;
};

export default StationDiagnosticTool;
